import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import OptimizedImage from "../components/OptimizedImage";
import { ChevronRight, Truck, Package, Scissors, CheckCircle, Flame, Droplets, Recycle, Heart } from "lucide-react";

const HomePage = () => {
  const { t } = useLanguage();
  const text = useSiteText();

  const services = [
    {
      icon: Truck,
      title: t.services.import.title,
      desc: t.services.import.desc,
      image: "https://meat-chain-pro.preview.emergentagent.com/api/uploads/fd9b249e79a7450c8d7b42a35ec75fee.jpg"
    },
    {
      icon: Package,
      title: t.services.carcass.title,
      desc: t.services.carcass.desc,
      image: "https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/dtp0kaax_R6II1460.JPG"
    },
    {
      icon: Scissors,
      title: t.services.processing.title,
      desc: t.services.processing.desc,
      image: "https://images.pexels.com/photos/7163991/pexels-photo-7163991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    }
  ];

  const qualityBadges = [
    t.quality.features.traceability,
    t.quality.features.hygiene,
    t.quality.features.consistency
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <OptimizedImage
            src={text("home.hero.background", "https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/7jzpd7du_DJI_0457.JPG")}
            alt="Tamari facility"
            priority
            sizes="100vw"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 mb-4">{text("home.hero.overline", t.hero.overline)}</p>
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {text("home.hero.title", t.hero.title)}
            </h1>
            <p className="text-lg md:text-xl text-zinc-200 mb-8 max-w-xl">
              {text("home.hero.subtitle", t.hero.subtitle)}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] border border-white px-8 py-4 font-medium hover:bg-transparent hover:text-white transition-colors"
                data-testid="hero-cta-button"
              >
                {text("home.hero.cta", t.hero.cta)}
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/services" 
                className="inline-flex items-center gap-2 bg-transparent text-white border border-white px-8 py-4 font-medium hover:bg-white hover:text-[#0A0A0A] transition-colors"
              >
                {text("home.hero.learn_more", t.hero.learnMore)}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.services}</p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.services.title}
              </h2>
              <Link to="/services" className="text-[#0A0A0A] font-medium flex items-center gap-2 hover:gap-3 transition-all">
                {t.common.seeAll} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-zinc-200">
            {services.map((service) => (
              <div key={service.title} className="bg-white" data-testid="service-card">
                <div className="relative h-64 overflow-hidden">
                  <OptimizedImage
                    src={service.image}
                    alt={service.title}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <service.icon className="w-8 h-8 text-white mb-3" />
                    <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {service.title}
                    </h3>
                  </div>
                </div>
                <div className="p-6 border border-t-0 border-zinc-200">
                  <p className="text-zinc-600 text-sm">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Salami Preview */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{t.nav.salami}</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.salami.title}
              </h2>
              <p className="text-zinc-600 text-lg mb-8">{t.salami.intro}</p>
              <Link 
                to="/salami" 
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 font-medium hover:bg-zinc-800 transition-colors"
              >
                {t.common.seeAll} <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative h-[400px] lg:h-[500px]">
              <OptimizedImage
                src="https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/foj3elbc_021A2415.JPG"
                alt="Salami products"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">{t.quality.title}</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.quality.subtitle}
              </h2>
              <div className="flex flex-wrap gap-3 mt-8">
                {qualityBadges.map((badge) => (
                  <div key={badge} className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-2 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[400px]">
              <OptimizedImage
                src="https://static.prod-images.emergentagent.com/jobs/a9df5f04-5208-4dad-bbe9-67be3549da0e/images/ce510c98b4320ab0eb8ba19fe8a0e84f2571f7776ed3e8e620c928f68048f6c6.png"
                alt="Quality network"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">{t.sustainability.overline}</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t.sustainability.title}
            </h2>
            <p className="text-xl text-zinc-600">{t.sustainability.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Flame, ...t.sustainability.features[0] },
              { icon: Droplets, ...t.sustainability.features[1] },
              { icon: Recycle, ...t.sustainability.features[2] },
              { icon: Heart, ...t.sustainability.features[3] }
            ].map((feature) => (
              <div key={feature.title} className="group">
                <div className="w-14 h-14 bg-emerald-50 flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
                  <feature.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-zinc-200">
            <p className="text-lg font-medium text-[#0A0A0A] flex items-center gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              {t.sustainability.closing}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.contact.title}
          </h2>
          <p className="text-zinc-600 text-lg mb-8 max-w-2xl mx-auto">{t.contact.subtitle}</p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-10 py-5 font-medium text-lg hover:bg-zinc-800 transition-colors"
          >
            {t.hero.cta} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
